import React from "react";
import { Box, Avatar, Typography, Grid, Chip, Stack } from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { API_BASE_URL } from "../../utils/api";

const ProfileInfoItem = ({ icon, label, value, valueColor = "#223142" }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.1 }}>
    <Box
      sx={{
        color: "#8a94a6",
        mt: "2px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {icon}
    </Box>

    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          color: "#8a94a6",
          fontWeight: 600,
          lineHeight: 1.2,
          mb: 0.25,
        }}
      >
        {label}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          color: valueColor,
          fontWeight: 700,
          lineHeight: 1.35,
          wordBreak: "break-word",
        }}
      >
        {value || "N/A"}
      </Typography>
    </Box>
  </Box>
);

const ProfileCard = ({ user }) => {
  const avatarSrc = user?.PhotoGraphicFileName
    ? `${API_BASE_URL}/memberimages/${encodeURIComponent(
        user.PhotoGraphicFileName
      )}`
    : "https://via.placeholder.com/80";

  const fullName = `${user?.Name || user?.FirstName || ""} ${
    user?.Lastname || user?.LastName || ""
  }`.trim();

  const joinedDate = user?.Joined || user?.JoinDate;
  const memberSince = joinedDate
    ? new Date(joinedDate).toLocaleDateString()
    : "N/A";

  const email = user?.Email || user?.EmailAddress || "N/A";
  const phone = user?.PhoneNumber || user?.Phone || "N/A";
  const address = user?.Address || user?.FullAddress || "N/A";

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "260px 1fr" },
          gap: { xs: 2.5, md: 4 },
          alignItems: "start",
        }}
      >
        {/* Left profile block */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: { xs: "center", md: "flex-start" },
            textAlign: { xs: "center", md: "left" },
          }}
        >
          <Avatar
            alt="Owner Avatar"
            src={avatarSrc}
            sx={{
              width: 74,
              height: 74,
              border: "3px solid rgba(47,99,173,0.12)",
              boxShadow: "0 8px 18px rgba(0,0,0,0.08)",
              mb: 1.75,
            }}
          />

          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#223142",
              lineHeight: 1.15,
              mb: 0.75,
            }}
          >
            {user ? fullName || "Unnamed Owner" : "Loading..."}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#8a94a6",
              mb: 1.5,
            }}
          >
            Member since {memberSince}
          </Typography>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Chip
              label="Active"
              size="small"
              sx={{
                height: 24,
                fontSize: 11,
                fontWeight: 700,
                color: "#2f63ad",
                backgroundColor: "rgba(47,99,173,0.10)",
                borderRadius: "999px",
              }}
            />
          </Stack>
        </Box>

        {/* Right details block */}
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6}>
            <Stack spacing={2}>
              <ProfileInfoItem
                icon={<EmailOutlinedIcon sx={{ fontSize: 18 }} />}
                label="Email"
                value={email}
              />
              <ProfileInfoItem
                icon={<CallOutlinedIcon sx={{ fontSize: 18 }} />}
                label="Phone"
                value={phone}
              />
            </Stack>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Stack spacing={2}>
              <ProfileInfoItem
                icon={<LocationOnOutlinedIcon sx={{ fontSize: 18 }} />}
                label="Address"
                value={address}
              />
              <ProfileInfoItem
                icon={<EventOutlinedIcon sx={{ fontSize: 18 }} />}
                label="Joined On"
                value={memberSince}
              />
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ProfileCard;